<template>
  <div>
    <span class="sr-only" :id="`${uniqueId}-menu-label`">{{ dropdownLabel }}</span>
    <div class="dropdown nav-item" v-click-outside="hideDropdown">
      <button
        class="unstyled-btn nav-link h4 dropdown-toggle dropdownMenuButton mb-0"
        :class="[
          btnClass,
          {
            'btn-primary': !showChevron,
            'dropdownMenuButton--has-chevron': showChevron,
            'dropdownMenuButton--open': showChevron && showDropdown,
          },
        ]"
        data-toggle="dropdown"
        :disabled="disabled"
        :id="`${uniqueId}-dropdown`"
        @click="toggleDropdown"
        @keydown.esc.exact.stop="hideDropdown"
        @keydown.shift.tab="hideDropdown"
        @keydown.up.exact.prevent="initialFocusWithArrowKeys"
        @keydown.down.exact.prevent="initialFocusWithArrowKeys"
        @keydown.tab.exact="focusedIndex = 0"
        aria-haspopup="listbox"
        :aria-labelledby="`${uniqueId}-menu-label ${uniqueId}-dropdown`"
        :aria-expanded="showDropdown ? 'true' : 'false'"
      >
        <font-awesome-icon v-if="btnIcon" :icon="btnIcon" class="dropdownMenuButton__icon" />
        {{ name }}
        <WCSvgIcon
          v-if="showChevron"
          name="chevron-down"
          class="dropdownMenuButton__chevron"
          :class="{ 'dropdownMenuButton__chevron--open': showDropdown }"
          :icon-size="{ widthRem: 0.625, heightRem: 0.4375 }"
        />
      </button>
      <div
        ref="dropdownMenus"
        class="dropdown-menu"
        :style="dropdownMenusStyle"
        v-if="showDropdown"
        :class="{ show: showDropdown }"
        role="listbox"
        :aria-labelledby="`${uniqueId}-menu-label`"
        :aria-activedescendant="focusedIndex >= 0 ? `${uniqueId}-option-${focusedIndex}` : null"
      >
        <button
          v-for="(option, index) in availableOptions"
          :key="option.id"
          :value="currentSelectedOption"
          :class="{
            selected: currentSelectedOption && currentSelectedOption.id == option.id,
            'font-weight-bold': option.isBold,
          }"
          :id="focusedIndex === index ? `${uniqueId}-option-${index}` : null"
          :aria-selected="true && currentSelectedOption && currentSelectedOption.id == option.id"
          class="dropdown-item btn-sm"
          @click="setSelectedOption(option)"
          @keydown.esc.exact.stop="hideDropdown"
          @keydown.tab.exact="
            index === availableOptions.length - 1 ? hideDropdown() : focusNext(false)
          "
          @keydown.shift.tab.exact="index === 0 ? (focusedIndex = -1) : focusPrevious(false)"
          @keydown.down.exact.prevent="index === availableOptions.length - 1 ? '' : focusNext(true)"
          @keydown.up.exact.prevent="index === 0 ? '' : focusPrevious(true)"
          role="option"
        >
          <font-awesome-icon v-if="option.icon" :icon="option.icon" class="dropdown-item__icon" />
          {{ option.name }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, useId } from 'vue';
import clickOutsidePlugin from 'click-outside-vue3';
import emitter from '@/eventBus';
import { SIDE_MENU_EVENTS } from '@/constants/EventConstants';

const vClickOutside = clickOutsidePlugin.directive;

const props = defineProps({
  currentSelectedOption: {
    type: Object,
    default: null,
  },
  availableOptions: {
    type: Array,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    required: true,
  },
  dropdownLabel: {
    type: String,
    required: true,
  },
  btnClass: {
    type: String,
  },
  unusableHeight: {
    required: false,
    type: String,
    default: '0px',
  },
  showChevron: {
    type: Boolean,
    default: false,
  },
  btnIcon: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['selectedOption']);

const uniqueId = useId();

const showDropdown = ref(false);
const focusedIndex = ref(-1);
const dropdownMenus = ref(null);

const dropdownMenusStyle = computed(
  () => `max-height: calc(100vh - ${props.unusableHeight}); overflow-y: auto;`,
);

function toggleDropdown() {
  showDropdown.value = !showDropdown.value;
}

function hideDropdown() {
  focusedIndex.value = -1;
  showDropdown.value = false;
}

function setSelectedOption(option) {
  emit('selectedOption', option);
  hideDropdown();
}

function initialFocusWithArrowKeys() {
  if (showDropdown.value && dropdownMenus.value?.children?.length) {
    focusedIndex.value = 0;
    dropdownMenus.value.children[0].focus();
  }
}

function focusItem() {
  if (dropdownMenus.value?.children?.length)
    dropdownMenus.value.children[focusedIndex.value].focus();
}

function focusPrevious(needFocus) {
  focusedIndex.value -= 1;
  if (needFocus) focusItem();
}

function focusNext(needFocus) {
  focusedIndex.value += 1;
  if (needFocus) focusItem();
}

onMounted(() => {
  emitter.$on(SIDE_MENU_EVENTS.OPEN, hideDropdown);
  emitter.$on(SIDE_MENU_EVENTS.TOGGLE, hideDropdown);
});

onUnmounted(() => {
  emitter.$off(SIDE_MENU_EVENTS.OPEN, hideDropdown);
  emitter.$off(SIDE_MENU_EVENTS.TOGGLE, hideDropdown);
});
</script>
<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.dropdown-item.selected,
.dropdown-item.active,
.dropdown-item:active,
.dropdown-item:focus,
.dropdown-item:hover {
  background-color: var(--secondary-lighter-7, get-color('secondary', 'lighter-7'));
  color: var(--secondary-contrast, get-color-contrast('secondary'));
}
.dropdown-item.selected {
  background-color: var(--secondary, get-color('secondary'));
}

.dropdownMenuButton--has-chevron {
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  background-color: transparent;

  &.dropdown-toggle::after {
    content: none;
  }
}

.dropdownMenuButton--open {
  background-color: var(--primary-darker-4, get-color('primary', 'darker-4'));
}

.dropdownMenuButton__chevron {
  color: var(--white, $white);
  transition: transform 0.3s ease;
}

.dropdownMenuButton__chevron--open {
  transform: rotate(180deg);
}

.dropdown-item__icon {
  width: 1.125rem;
  margin-right: 0.625rem;
  color: var(--primary, get-color('primary'));
}
</style>
